import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { agrogestao } from '@/api/agrogestaoClient';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";

export function ProdutoresAdmin() {
    const [busca, setBusca] = useState('');
    const [plano, setPlano] = useState('todos');
    const [status, setStatus] = useState('todos');
    const [periodoTipo, setPeriodoTipo] = useState('cadastro');
    const [inicio, setInicio] = useState('');
    const [fim, setFim] = useState('');
    const [page, setPage] = useState(1);
    const pageSize = 20;

    const { data: produtores = [], isLoading } = useQuery({
        queryKey: ['users_admin_prod'],
        queryFn: async () => {
            const { data, error } = await agrogestao.client
                .from('users')
                .select('email, full_name, created_date, role')
                .order('created_date', { ascending: false })
                .limit(2000);
            if (error) return [];
            return data || [];
        }
    });

    const { data: propriedades = [] } = useQuery({
        queryKey: ['propriedades_admin_all'],
        queryFn: async () => {
            const { data, error } = await agrogestao.client
                .from('propriedades')
                .select('id, nome, created_by, created_date')
                .order('created_date', { ascending: false })
                .limit(2000);
            if (error) return [];
            return data || [];
        }
    });

    const propIds = useMemo(() => propriedades.map(p => p.id).filter(Boolean), [propriedades]);

    const { data: licencas = [] } = useQuery({
        queryKey: ['licencas_admin_all', propIds.join(',')],
        queryFn: async () => {
            if (!propIds || propIds.length === 0) return [];
            const { data, error } = await agrogestao.client
                .from('licencas')
                .select('id, propriedade_id, plano, status, valid_until')
                .in('propriedade_id', propIds)
                .order('valid_until', { ascending: false })
                .limit(4000);
            if (error) return [];
            return data || [];
        },
        enabled: propIds.length > 0
    });

    const { data: auditUsers = [] } = useQuery({
        queryKey: ['audit_logs_users'],
        queryFn: async () => {
            const in90 = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString();
            const { data, error } = await agrogestao.client
                .from('audit_logs')
                .select('user_email, acao, created_at')
                .gte('created_at', in90)
                .order('created_at', { ascending: false })
                .limit(5000);
            if (error) return [];
            return data || [];
        }
    });

    const ultimoLogin = useMemo(() => {
        const map = {};
        (auditUsers || []).filter(a => String(a.acao || '').toLowerCase().includes('login')).forEach(a => {
            const key = String(a.user_email || '');
            const cur = map[key] ? new Date(map[key]) : new Date(0);
            const d = a.created_at ? new Date(a.created_at) : new Date(0);
            if (d > cur) map[key] = d.toISOString();
        });
        return map;
    }, [auditUsers]);

    const acessos90d = useMemo(() => {
        const map = {};
        (auditUsers || []).filter(a => String(a.acao || '').toLowerCase().includes('login')).forEach(a => {
            const key = String(a.user_email || '');
            map[key] = (map[key] || 0) + 1;
        });
        return map;
    }, [auditUsers]);

    const propsByUser = useMemo(() => {
        const map = {};
        propriedades.forEach(p => {
            const key = String(p.created_by || '');
            if (!map[key]) map[key] = [];
            map[key].push(p);
        });
        return map;
    }, [propriedades]);

    const licencasByProp = useMemo(() => {
        const map = {};
        licencas.forEach(l => {
            const key = String(l.propriedade_id || '');
            if (!map[key]) map[key] = [];
            map[key].push(l);
        });
        return map;
    }, [licencas]);

    const licencaAtualPorProp = useMemo(() => {
        const map = {};
        propriedades.forEach(p => {
            const list = licencasByProp[String(p.id || '')] || [];
            const ativa = list.find(l => l.status === 'ativo');
            if (ativa) map[String(p.id || '')] = ativa;
            else if (list.length > 0) map[String(p.id || '')] = list[0];
        });
        return map;
    }, [propriedades, licencasByProp]);

    const propsById = useMemo(() => {
        const map = {};
        propriedades.forEach(p => { map[String(p.id || '')] = p; });
        return map;
    }, [propriedades]);

    const hoje = new Date();

    const filtered = useMemo(() => {
        return (produtores || []).filter(u => {
            const email = String(u.email || '');
            const props = propsByUser[email] || [];
            const licsUser = props.map(p => licencaAtualPorProp[String(p.id || '')]).filter(Boolean);
            const hasActive = licsUser.some(l => {
                const exp = l.valid_until ? new Date(l.valid_until) : null;
                return l.status === 'ativo' && (exp ? exp >= hoje : true);
            });

            if (plano !== 'todos') {
                if (licsUser.length === 0 && plano !== 'sem_plano') return false;
                if (licsUser.length > 0 && !licsUser.some(l => l.plano === plano)) return false;
            }
            if (status !== 'todos') {
                if (status === 'ativo' && !hasActive) return false;
                if (status === 'sem_plano' && (licsUser.length > 0)) return false;
            }

            if (busca) {
                const b = busca.toLowerCase();
                const campos = [String(u.full_name || ''), String(u.email || '')].map(x => x.toLowerCase());
                if (!campos.some(c => c.includes(b))) return false;
            }

            if (inicio || fim) {
                const d = periodoTipo === 'cadastro' ? (u.created_date ? new Date(u.created_date) : null) : (ultimoLogin[email] ? new Date(ultimoLogin[email]) : null);
                if (inicio && d && d < new Date(inicio)) return false;
                if (fim && d && d > new Date(fim)) return false;
            }
            return true;
        });
    }, [produtores, propsByUser, licencaAtualPorProp, plano, status, busca, inicio, fim, periodoTipo, ultimoLogin]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const pageData = useMemo(() => {
        const start = (page - 1) * pageSize;
        return filtered.slice(start, start + pageSize);
    }, [filtered, page]);

    const [detalheOpen, setDetalheOpen] = useState(false);
    const [userSel, setUserSel] = useState(null);
    const [userSelFull, setUserSelFull] = useState(null);
    const [userSelLoading, setUserSelLoading] = useState(false);
    const [auditUserSel, setAuditUserSel] = useState([]);

    const carregarDetalhes = async (u) => {
        setUserSel(u);
        setDetalheOpen(true);
        setUserSelLoading(true);
        setAuditUserSel([]);
        try {
            const { data: full } = await agrogestao.client.from('users').select('*').eq('email', u.email).maybeSingle();
            setUserSelFull(full || u);

            const in90 = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString();
            const { data: aud } = await agrogestao.client
                .from('audit_logs')
                .select('*')
                .eq('user_email', u.email)
                .gte('created_at', in90)
                .order('created_at', { ascending: false })
                .limit(100);
            setAuditUserSel(aud || []);
        } catch { }
        setUserSelLoading(false);
    };

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
                <Input placeholder="Buscar nome ou email" value={busca} onChange={(e) => setBusca(e.target.value)} />
                <Select value={plano} onValueChange={setPlano}>
                    <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="todos">Todos Planos</SelectItem>
                        <SelectItem value="trial">Trial</SelectItem>
                        <SelectItem value="mensal">Mensal</SelectItem>
                        <SelectItem value="anual">Anual</SelectItem>
                        <SelectItem value="sem_plano">Sem Plano</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="todos">Todos Status</SelectItem>
                        <SelectItem value="ativo">Ativo</SelectItem>
                        <SelectItem value="sem_plano">Sem Licença</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={periodoTipo} onValueChange={setPeriodoTipo}>
                    <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="cadastro">Período: Cadastro</SelectItem>
                        <SelectItem value="ultimo_login">Período: Último Login</SelectItem>
                    </SelectContent>
                </Select>
                <div className="flex items-center gap-2">
                    <Label>Início</Label>
                    <Input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} />
                </div>
                <div className="flex items-center gap-2">
                    <Label>Fim</Label>
                    <Input type="date" value={fim} onChange={(e) => setFim(e.target.value)} />
                </div>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className="border-b">
                            <th className="text-left py-3 px-4">Nome</th>
                            <th className="text-left py-3 px-4">Email</th>
                            <th className="text-left py-3 px-4">Propriedades</th>
                            <th className="text-left py-3 px-4">Planos</th>
                            <th className="text-left py-3 px-4">Cadastro</th>
                            <th className="text-left py-3 px-4">Último Login</th>
                            <th className="text-left py-3 px-4">Acessos (90d)</th>
                            <th className="text-left py-3 px-4">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr><td colSpan={8} className="py-6 text-center">Carregando...</td></tr>
                        ) : pageData.length === 0 ? (
                            <tr><td colSpan={8} className="py-6 text-center text-stone-500">Nenhum produtor encontrado</td></tr>
                        ) : pageData.map((u) => {
                            const props = propsByUser[String(u.email || '')] || [];
                            const lics = props.map(p => licencaAtualPorProp[String(p.id || '')]).filter(Boolean);
                            const planos = Array.from(new Set(lics.map(l => l.plano))).join(', ') || '-';
                            const ult = ultimoLogin[String(u.email || '')];
                            const acs = acessos90d[String(u.email || '')] || 0;
                            return (
                                <tr key={u.email} className="border-b">
                                    <td className="py-3 px-4 font-medium">{u.full_name || '-'}</td>
                                    <td className="py-3 px-4 text-stone-600">{u.email || '-'}</td>
                                    <td className="py-3 px-4">{props.length}</td>
                                    <td className="py-3 px-4">{planos}</td>
                                    <td className="py-3 px-4">{u.created_date ? format(new Date(u.created_date), 'dd/MM/yyyy') : '-'}</td>
                                    <td className="py-3 px-4">{ult ? format(new Date(ult), 'dd/MM/yyyy HH:mm') : '-'}</td>
                                    <td className="py-3 px-4">{acs}</td>
                                    <td className="py-3 px-4">
                                        <Button variant="outline" onClick={() => carregarDetalhes(u)}>Ver detalhes</Button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <div className="flex items-center gap-2 justify-end">
                <Button onClick={() => setPage(p => Math.max(1, p - 1))} variant="outline">Anterior</Button>
                <div className="text-sm">Página {page} de {totalPages}</div>
                <Button onClick={() => setPage(p => Math.min(totalPages, p + 1))} variant="outline">Próxima</Button>
            </div>
            <Dialog open={detalheOpen} onOpenChange={setDetalheOpen}>
                <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Detalhes do Produtor</DialogTitle>
                        <DialogDescription></DialogDescription>
                    </DialogHeader>
                    {userSel && (
                        <div className="space-y-4">
                            {userSelLoading && <div className="text-center py-4">Carregando dados completos...</div>}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Card className="bg-emerald-50 border-emerald-200">
                                    <CardHeader><CardTitle className="text-sm">Dados Cadastrais</CardTitle></CardHeader>
                                    <CardContent className="space-y-1">
                                        <div className="text-sm font-medium">{userSelFull?.full_name || userSel.full_name}</div>
                                        <div className="text-sm">{userSelFull?.email || userSel.email}</div>
                                        <div className="text-sm">Criado: {userSel.created_date ? format(new Date(userSel.created_date), 'dd/MM/yyyy HH:mm') : '-'}</div>
                                        <div className="text-sm">Tipo: {userSelFull?.tipo_pessoa || '-'}</div>
                                        <div className="text-sm">CPF: {userSelFull?.cpf || '-'}</div>
                                        <div className="text-sm">CNPJ: {userSelFull?.cnpj || '-'}</div>
                                        <div className="text-sm">Telefone: {userSelFull?.telefone || '-'}</div>
                                        <div className="text-sm">WhatsApp: {userSelFull?.whatsapp || '-'}</div>
                                        <div className="text-sm">Contato: {userSelFull?.contato_nome || '-'}</div>
                                        <div className="text-sm">Endereço: {(() => { const d = userSelFull; return d ? `${d.endereco_logradouro || ''}, ${d.endereco_numero || ''} ${d.endereco_complemento || ''} - ${d.endereco_bairro || ''} - ${d.endereco_cidade || ''}/${d.endereco_estado || ''} ${d.endereco_cep || ''}` : '-'; })()}</div>
                                    </CardContent>
                                </Card>
                                <Card className="bg-indigo-50 border-indigo-200">
                                    <CardHeader><CardTitle className="text-sm">Resumo</CardTitle></CardHeader>
                                    <CardContent>
                                        {(() => { const props = propsByUser[String(userSel.email || '')] || []; const licAtual = props.map(p => licencaAtualPorProp[String(p.id || '')]).filter(Boolean); const ativas = licAtual.filter(l => { const exp = l.valid_until ? new Date(l.valid_until) : null; return l.status === 'ativo' && (exp ? exp >= new Date() : true); }).length; return (<div className="space-y-1"><div className="text-sm">Propriedades: {props.length}</div><div className="text-sm">Licenças: {licAtual.length}</div><div className="text-sm">Ativas: {ativas}</div></div>); })()}
                                    </CardContent>
                                </Card>
                            </div>
                            <Card className="bg-rose-50 border-rose-200">
                                <CardHeader><CardTitle className="text-sm">Histórico de Ações</CardTitle></CardHeader>
                                <CardContent>
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead>
                                                <tr className="border-b">
                                                    <th className="text-left py-2 px-2">Data</th>
                                                    <th className="text-left py-2 px-2">Ação</th>
                                                    <th className="text-left py-2 px-2">Tabela</th>
                                                    <th className="text-left py-2 px-2">Propriedade</th>
                                                    <th className="text-left py-2 px-2">Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(auditUserSel || []).length === 0 ? (
                                                    <tr><td colSpan={5} className="py-4 text-center text-stone-500">Sem ações recentes</td></tr>
                                                ) : (auditUserSel || []).map((r, idx) => (
                                                    <tr key={idx} className="border-b">
                                                        <td className="py-2 px-2">{r.created_at ? format(new Date(r.created_at), 'dd/MM/yyyy HH:mm') : '-'}</td>
                                                        <td className="py-2 px-2">{r.acao || '-'}</td>
                                                        <td className="py-2 px-2">{r.table_name || '-'}</td>
                                                        <td className="py-2 px-2">{(() => { const p = propsById[String(r.propriedade_id || '')]; return p?.nome || String(r.propriedade_id || '-'); })()}</td>
                                                        <td className="py-2 px-2">
                                                            <Badge className={r.status === 'success' ? 'bg-green-100 text-green-700' : r.status === 'denied' ? 'bg-red-100 text-red-700' : 'bg-stone-100 text-stone-700'}>{r.status || '-'}</Badge>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </CardContent>
                            </Card>
                            <Card className="bg-lime-50 border-lime-200">
                                <CardHeader><CardTitle className="text-sm">Propriedades</CardTitle></CardHeader>
                                <CardContent>
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead>
                                                <tr className="border-b">
                                                    <th className="text-left py-2 px-2">Propriedade</th>
                                                    <th className="text-left py-2 px-2">Licença</th>
                                                    <th className="text-left py-2 px-2">Plano</th>
                                                    <th className="text-left py-2 px-2">Status</th>
                                                    <th className="text-left py-2 px-2">Vencimento</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(() => {
                                                    const props = propsByUser[String(userSel.email || '')] || [];
                                                    if (props.length === 0) return (<tr><td colSpan={5} className="py-4 text-center text-stone-500">Nenhuma propriedade</td></tr>);
                                                    return props.map(p => {
                                                        const lic = licencaAtualPorProp[String(p.id || '')];
                                                        return (
                                                            <tr key={p.id} className="border-b">
                                                                <td className="py-2 px-2">{p.nome || String(p.id || '')}</td>
                                                                <td className="py-2 px-2 font-mono text-xs">{String(lic?.id || '-')}</td>
                                                                <td className="py-2 px-2">{lic?.plano || '-'}</td>
                                                                <td className="py-2 px-2">{lic?.status || '-'}</td>
                                                                <td className="py-2 px-2">{lic?.valid_until ? format(new Date(lic.valid_until), 'dd/MM/yyyy') : '-'}</td>
                                                            </tr>
                                                        );
                                                    });
                                                })()}
                                            </tbody>
                                        </table>
                                    </div>
                                </CardContent>
                            </Card>
                            <Card className="bg-violet-50 border-violet-200">
                                <CardHeader><CardTitle className="text-sm">Licenças por Propriedade</CardTitle></CardHeader>
                                <CardContent>
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead>
                                                <tr className="border-b">
                                                    <th className="text-left py-2 px-2">Propriedade</th>
                                                    <th className="text-left py-2 px-2">Licenças</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(() => {
                                                    const props = propsByUser[String(userSel.email || '')] || [];
                                                    if (props.length === 0) return (<tr><td colSpan={2} className="py-4 text-center text-stone-500">Nenhuma licença</td></tr>);
                                                    return props.map(p => {
                                                        const list = licencasByProp[String(p.id || '')] || [];
                                                        return (
                                                            <tr key={p.id} className="border-b">
                                                                <td className="py-2 px-2">{p.nome || String(p.id || '')}</td>
                                                                <td className="py-2 px-2 text-xs">
                                                                    {list.length === 0 ? '-' : list.sort((a, b) => {
                                                                        const av = a.valid_until ? new Date(a.valid_until).getTime() : 0;
                                                                        const bv = b.valid_until ? new Date(b.valid_until).getTime() : 0;
                                                                        return bv - av;
                                                                    }).map(l => `${String(l.id)} • ${l.plano} • ${l.status} • ${l.valid_until ? format(new Date(l.valid_until), 'dd/MM/yyyy') : '-'}`).join(' | ')}
                                                                </td>
                                                            </tr>
                                                        );
                                                    });
                                                })()}
                                            </tbody>
                                        </table>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
